import {
  buildAlternateUrlForLang,
  buildHreflangAlternates,
  type HreflangAlternateEntry
} from '@bcf-vanilla-shared-ts-v1/seo/build-hreflang-alternates';
import type {
  DomainSettings,
  DomainSettingsCountry
} from '@bcf-vanilla-ts-v1-api-access/ext-api/models/domain-settings';
import type { CmsSeoMetaUpdaterChannels } from '@bcf-vanilla-ts-v1-logic-worker-cms/bloc/cms-seo-meta-updater/cms-seo-meta-updater-channels';
import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { pathJoin } from '@bcf-vanilla-ts-v1-shared/misc/pure-utils/path-join';
import { transferRxNext } from '@bcf-vanilla-ts-v1-shared/misc/rx-helpers/transfer-rx-next';
import { ENV_BASE } from '@bcf2-envs/env-token';
import type { EnvBase } from '@bcf2-envs/types';
import {
  provideBcfActivatedUrl,
  type BcfActivatedUrl
} from '@bcf2-shared-pkg-v1/ui/shared/bcf-router/bcf-activated-url';
import { UnsubscribableWithWorkerBlocForService } from '@bcf2-v1-platforms/platform-worker/browser/unsubscribable-with-worker-bloc-for-service';
import { getAppLangSegment } from '@bcf2-v1-ui-routes/app-lang-segment';
import type { ReactiveControllerHost } from 'lit';
import { combineLatest, of } from 'rxjs';
import { provideMetaService, type MetaService } from '../meta.service';
import { provideTitleService, type TitleService } from '../title.service';

export class CmsSeoMetaUpdaterService extends UnsubscribableWithWorkerBlocForService<CmsSeoMetaUpdaterChannels> {
  constructor(
    private _activatedUrl: BcfActivatedUrl,
    private _metaService: MetaService,
    private _titleService: TitleService,
    private _location: Location,
    private _document: Document,
    private _envBase: EnvBase,
    host: ReactiveControllerHost
  ) {
    super(host);
    this._initWorkerBloc('cms-seo-meta-updater');
  }

  public override init(): void {
    this._updateHtmlLang();
    this._sub = this._subTo('metaTitle').subscribe((title: string) => this._titleService.setTitle(title));

    this._sub = this._subTo('metaDescription').subscribe((description: string) =>
      this._metaService.updateTag({ name: 'description', content: description })
    );

    this._sub = this._subTo('metaRobots').subscribe((robots: string) =>
      this._metaService.updateTag({ name: 'robots', content: robots })
    );

    this._sub = combineLatest([
      this._subTo('metaCanonical'),
      of(getAppLangSegment()),
      this._activatedUrl.fullUrl$,
      this._subTo('domainSettings')
    ]).subscribe(this._updateCanonicalUrl());

    this._sub = combineLatest([this._activatedUrl.fullUrl$, this._subTo('domainSettings')]).subscribe(
      this._updateHreflang()
    );
  }

  public override _initializedBloc(): void {
    this._workerSubject('currentHostname$').next(this._location.hostname);
    this._sub = this._activatedUrl.url$.subscribe(transferRxNext(this._workerSubject('currentUrl$')));
    this._sub = this._activatedUrl.fullUrl$.subscribe(transferRxNext(this._workerSubject('currentFullUrl$')));
  }

  private _updateCanonicalUrl() {
    return ([urlPathname, currentLang, fullUrl, domainSettings]: [
      string,
      string,
      string,
      DomainSettings | undefined
    ]): void => {
      const baseHref: string = this._baseHref;
      const head: HTMLHeadElement = this._document.head;
      let element: HTMLLinkElement | null = this._document.querySelector(`link[rel='canonical']`);
      if (!element) {
        element = this._document.createElement('link') as HTMLLinkElement;
        element.setAttribute('rel', 'canonical');
        head.appendChild(element);
      }

      const currentUrl: URL = new URL(fullUrl);
      const currentHost: string = currentUrl.hostname;

      let canonicalHost: string = currentHost;

      if (domainSettings) {
        const langSettings: DomainSettingsCountry | undefined = domainSettings[currentLang.toUpperCase()];

        if (langSettings && langSettings.activeDomain) {
          canonicalHost = new URL('https://' + langSettings.activeDomain).hostname;
        }
      }

      const canonicalPath: string = pathJoin(baseHref, urlPathname);
      const newCanonicalUrl: URL = new URL(canonicalPath, `https://${canonicalHost}`);
      element.setAttribute('href', newCanonicalUrl.toString());
    };
  }

  private get _baseHref(): string {
    const baseElement: HTMLBaseElement | null = this._document.querySelector('base');
    return baseElement ? new URL(baseElement.href).pathname : '/';
  }

  private _updateHreflang() {
    return ([url, domainSettings]: [string, DomainSettings | undefined]): void => {
      const currentHost: string = new URL(url).hostname.replace(/\d+(?=\.[^.]+$)/, '');
      const pagePathname: string = pathJoin(this._baseHref, new URL(url).pathname);
      const relationLinks: HTMLLinkElement[] = Array.from(this._document.querySelectorAll(`link[rel='alternate']`));

      const alternates: HreflangAlternateEntry[] = this._envBase.availableLangs.map((lang: string) => {
        const activeDomain: string = domainSettings?.[lang.toUpperCase()]?.activeDomain ?? currentHost;

        return {
          lang,
          url: buildAlternateUrlForLang(pagePathname, lang, activeDomain, this._envBase.availableLangs)
        };
      });

      const hreflangAlternates: HreflangAlternateEntry[] = buildHreflangAlternates({
        url,
        alternates,
        domainSettings
      });

      for (const alternate of hreflangAlternates) {
        const existingLink: HTMLLinkElement | undefined = relationLinks.find(
          (link: HTMLLinkElement) => link.hreflang === alternate.lang
        );

        if (existingLink) {
          existingLink.setAttribute('href', alternate.url);
          continue;
        }

        const element: HTMLLinkElement = this._document.createElement('link') as HTMLLinkElement;
        this._document.head.appendChild(element);
        element.setAttribute('rel', 'alternate');
        element.setAttribute('hreflang', alternate.lang);
        element.setAttribute('href', alternate.url);
      }
    };
  }

  private _updateHtmlLang() {
    return (lang: string): void => {
      const htmlTag: HTMLHtmlElement = this._document.querySelector('html')!;
      htmlTag.lang = lang;
    };
  }
}
export function provideCmsSeoMetaUpdaterService(host: ReactiveControllerHost): CmsSeoMetaUpdaterService {
  return new CmsSeoMetaUpdaterService(
    provideBcfActivatedUrl(),
    provideMetaService(),
    provideTitleService(),
    window.location,
    document,
    provideDiTokenValue(ENV_BASE),
    host
  );
}
